import * as React from "react";
import {
  isElementFullscreen,
  enterBrowserFullscreen,
  exitBrowserFullscreen,
  enterViewportFullscreen,
  exitViewportFullscreen,
  onFullscreenChange,
  type FullscreenStyleOptions,
} from "../utils/fullscreen";

export type FullscreenMode = "viewport" | "browser";

export interface UseFullscreenOptions {
  /** 全屏方式：视窗内全屏 或 浏览器原生全屏 */
  mode?: FullscreenMode;
  /** 视窗内全屏时的容器，默认 document.body */
  container?: HTMLElement | null;
  /** 视窗内全屏时的样式选项 */
  styleOptions?: FullscreenStyleOptions;
  /** 进入全屏回调 */
  onEnter?: () => void;
  /** 退出全屏回调 */
  onExit?: () => void;
}

/**
 * 对 ref 指向的 DOM 元素做全屏控制（视窗内全屏或浏览器全屏）。
 * @param targetRef - 要全屏的元素的 ref（可传 null 表示暂不绑定）
 * @param options - 全屏方式与回调
 * @returns 当前是否全屏、进入/退出/切换方法
 */
export function useFullscreen(
  targetRef: React.RefObject<HTMLElement | null>,
  options: UseFullscreenOptions = {}
) {
  const {
    mode = "viewport",
    container = null,
    styleOptions,
    onEnter,
    onExit,
  } = options;

  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const isViewport = mode === "viewport";

  const updateState = React.useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    setIsFullscreen(isElementFullscreen(el));
  }, [targetRef]);

  React.useEffect(() => {
    const el = targetRef.current;
    if (!el) return undefined;
    updateState();
    if (!isViewport) {
      return onFullscreenChange(() => updateState());
    }
    const interval = setInterval(updateState, 150);
    return () => clearInterval(interval);
  }, [targetRef, isViewport, updateState]);

  const enter = React.useCallback(async () => {
    const el = targetRef.current;
    if (!el) return;
    try {
      if (isViewport) {
        enterViewportFullscreen(el, container ?? document.body, styleOptions);
        onEnter?.();
      } else {
        await enterBrowserFullscreen(el);
        onEnter?.();
      }
      updateState();
    } catch (err) {
      console.error("useFullscreen enter error", err);
    }
  }, [targetRef, isViewport, container, styleOptions, onEnter, updateState]);

  const exit = React.useCallback(async () => {
    try {
      if (isViewport) {
        exitViewportFullscreen();
        onExit?.();
      } else {
        await exitBrowserFullscreen();
        onExit?.();
      }
      updateState();
    } catch (err) {
      console.error("useFullscreen exit error", err);
    }
  }, [isViewport, onExit, updateState]);

  const toggle = React.useCallback(async () => {
    const el = targetRef.current;
    if (!el) return;
    if (isElementFullscreen(el)) {
      await exit();
    } else {
      await enter();
    }
  }, [targetRef, enter, exit]);

  return {
    isFullscreen,
    enter,
    exit,
    toggle,
  };
}
